 
import { Observable } from 'rxjs';
import { CompletionMessage, Conversation, Assistant } from '@app-libs/model';

/**
 * Chat types enumeration
 */
export enum ChatType {
  Chat = 'chat',
  Assistant = 'assistant',
  Conversation = 'conversation',
}

/**
 * Chat initialization parameters
 */
export interface ChatInitParams {
  chatType: ChatType;
  id?: string;
  workspaceId?: string;
}

/**
 * Chat orchestration interface
 */
export interface IChatOrchestrator {
  /**
   * Send message with streaming response
   */
  sendMessageStream(message: Partial<CompletionMessage>): Observable<CompletionMessage>;

  /**
   * Stop current streaming operation
   */
  stopStream(): void;

  /**
   * Initialize chat context
   */
  initChat(params: ChatInitParams): Promise<void>;

  /**
   * Reset chat state
   */
  resetChat(): void;

  /**
   * Get current chat state
   */
  getCurrentState(): ChatOperationState;
}

/**
 * Chat operation state
 */
export interface ChatOperationState {
  processing: boolean;
  messages: Partial<CompletionMessage>[];
  selectedModel: string;
  workspaceId: string;
  chatType: ChatType;
  conversation: Conversation | null;
  assistant: Assistant | null;
}

/**
 * Chat operation types
 */
export enum ChatOperation {
  SENDING_MESSAGE = 'sending_message',
  STREAMING_RESPONSE = 'streaming_response',
  GENERATING_TITLE = 'generating_title',
  UPLOADING_FILE = 'uploading_file',
  TRANSCRIBING_AUDIO = 'transcribing_audio',
  INITIALIZING_CHAT = 'initializing_chat',
}

/**
 * Chat initialization result
 */
export interface ChatInitResult {
  success: boolean;
  conversation?: Conversation;
  assistant?: Assistant;
  messages: Partial<CompletionMessage>[];
  error?: string;
}

/**
 * Message sending result
 */
export interface MessageSendResult {
  userMessage: CompletionMessage;
  assistantMessage: CompletionMessage;
  conversation: Conversation;
  success: boolean;
  error?: string;
}

/**
 * Chat events
 */
export enum ChatEventType {
  MESSAGE_SENT = 'message_sent',
  MESSAGE_RECEIVED = 'message_received',
  STREAM_STARTED = 'stream_started',
  STREAM_CHUNK = 'stream_chunk',
  STREAM_COMPLETED = 'stream_completed',
  STREAM_ERROR = 'stream_error',
  CHAT_INITIALIZED = 'chat_initialized',
  CHAT_RESET = 'chat_reset',
}

/**
 * Chat event data
 */
export interface ChatEvent {
  type: ChatEventType;
  data?: any;
  timestamp: number;
  chatId?: string;
}

/**
 * Error types
 */
export enum ChatErrorType {
  NETWORK_ERROR = 'network_error',
  VALIDATION_ERROR = 'validation_error',
  AUTHENTICATION_ERROR = 'authentication_error',
  RATE_LIMIT_ERROR = 'rate_limit_error',
  STREAM_ERROR = 'stream_error',
  UNKNOWN_ERROR = 'unknown_error',
}

/**
 * Chat error details
 */
export interface ChatError {
  type: ChatErrorType;
  message: string;
  details?: any;
  timestamp: number;
  retryable: boolean;
}
